import React, { useEffect } from 'react';
import { demeterAnalyticsApi, demeterUsersApi } from '../../../../Apis/Apis';
import { AnalyticsReportType, DemeterUserModel } from '../../../../Generated/Raven-Demeter';
import useApi from '../../../Apis/Hooks/useApiHook';
import useApiWithoutAutoExecute from '../../../Apis/Hooks/useApiWithoutAutoExecute';
import LinkButton, { LinkButtonType } from '../../../Components/Form/Buttons/LinkButton';
import DateTimePickerInput from '../../../Components/Form/Inputs/DateTimePickerInput';
import Dropdown from '../../../Components/Form/Inputs/Dropdown';
import { SelectInputOption } from '../../../Components/Form/Inputs/SelectInput';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from './AnalyticsPage.module.scss';
import { defaultDateFrom, defaultDateTo, defaultLimit, defaultReportType, limits, reportTypes } from './AnalyticsPageDefinitions';
import AnalyticsReport from './AnalyticsReport';

const AnalyticsPage: React.FC = () => {
    const [translations] = useLanguage();
    const [selectedReportType, setSelectedReportType] = React.useState<AnalyticsReportType | undefined>(defaultReportType);
    const [selectedDateFrom, setSelectedDateFrom] = React.useState<Date | null | undefined>(defaultDateFrom);
    const [selectedDateTo, setSelectedDateTo] = React.useState<Date | null | undefined>(defaultDateTo);
    const [selectedLimit, setSelectedLimit] = React.useState<number | undefined>(defaultLimit);
    const [selectedUserGuid, setSelectedUserGuid] = React.useState<string | undefined>();
    const [users, setUsers] = React.useState<DemeterUserModel[]>([]);

    const reportTypeOptions: SelectInputOption<AnalyticsReportType>[] = reportTypes.map((reportType) => ({
        label: translations.analyticsReportType[reportType] || reportType,
        value: reportType,
    }));

    const limitOptions: SelectInputOption<number>[] = limits.map((limit) => ({
        label: limit.toString(),
        value: limit,
    }));

    const userOptions: SelectInputOption<string | undefined>[] = users.map((user) => ({
        label: `${user.firstName} ${user.lastName} (${user.email})`,
        value: user.demeterUserGuid ?? undefined,
    }));

    const [isLoading, getDemeterAnalyticsReport, getDemeterAnalyticsReportResponse] = useApiWithoutAutoExecute(() => {
        if (!selectedReportType || !selectedDateFrom || !selectedDateTo || !selectedLimit) {
            return null;
        }
        return demeterAnalyticsApi.getDemeterAnalyticsReport(
            selectedReportType,
            selectedDateFrom.toISOString(),
            selectedDateTo?.toISOString(),
            selectedLimit,
            selectedUserGuid,
        );
    });

    const [, , listDemeterUsersResponse] = useApi(() => demeterUsersApi.listDemeterUsers());

    useEffect(() => {
        if (listDemeterUsersResponse?.rows?.length) {
            const userList = listDemeterUsersResponse.rows
                .filter((u) => u.demeterUserGuid)
                .sort((a, b) => {
                    const fullNameA = `${a.firstName ?? ''}${a.lastName ?? ''}${a.email ?? ''}`.trim().toLowerCase();
                    const fullNameB = `${b.firstName ?? ''}${b.lastName ?? ''}${b.email ?? ''}`.trim().toLowerCase();
                    return fullNameA.localeCompare(fullNameB, undefined, { sensitivity: 'base' });
                });

            setUsers(userList);
            setSelectedUserGuid(userList[0]?.demeterUserGuid ?? undefined);
        }
    }, [listDemeterUsersResponse]);

    const showUsersDropdown =
        userOptions &&
        userOptions.length > 0 &&
        (selectedReportType === AnalyticsReportType.BackendUserRequests ||
            selectedReportType === AnalyticsReportType.FrontendUserPageViews ||
            selectedReportType === AnalyticsReportType.FrontendUserDetails ||
            selectedReportType === AnalyticsReportType.FrontendUserClicks ||
            selectedReportType === AnalyticsReportType.FrontendUserClicksCount);

    return (
        <div className={styles.analytics_container}>
            <div className={styles.analytics_toolbar}>
                <div className={styles.analytics_toolbar_item}>
                    <Dropdown
                        value={selectedReportType}
                        options={reportTypeOptions}
                        handleOptionChange={setSelectedReportType}
                        track={{
                            eventCategory: 'Analytics',
                            eventAction: 'ReportTypeChanged',
                            eventValue: selectedReportType,
                        }}
                    />
                </div>
                <div className={styles.analytics_toolbar_item}>
                    <DateTimePickerInput
                        value={selectedDateFrom}
                        handleChange={setSelectedDateFrom}
                        track={{
                            eventCategory: 'Analytics',
                            eventAction: 'DateFromChanged',
                            eventValue: selectedDateFrom,
                        }}
                    />
                </div>
                <div className={styles.analytics_toolbar_item}>
                    <DateTimePickerInput
                        value={selectedDateTo}
                        handleChange={setSelectedDateTo}
                        track={{
                            eventCategory: 'Analytics',
                            eventAction: 'DateToChanged',
                            eventValue: selectedDateTo,
                        }}
                    />
                </div>
                <div className={styles.analytics_toolbar_item}>
                    <Dropdown
                        value={selectedLimit}
                        options={limitOptions}
                        handleOptionChange={setSelectedLimit}
                        track={{
                            eventCategory: 'Analytics',
                            eventAction: 'LimitChanged',
                            eventValue: selectedLimit,
                        }}
                    />
                </div>
                {showUsersDropdown && (
                    <div className={styles.analytics_toolbar_item}>
                        <Dropdown
                            value={selectedUserGuid}
                            options={userOptions}
                            handleOptionChange={setSelectedUserGuid}
                            track={{
                                eventCategory: 'Analytics',
                                eventAction: 'UserChanged',
                                eventValue: selectedUserGuid,
                            }}
                        />
                    </div>
                )}
                <div className={styles.analytics_toolbar_item}>
                    <LinkButton
                        title="Run"
                        type={LinkButtonType.Blue}
                        onClick={getDemeterAnalyticsReport}
                        track={{ eventCategory: 'Analytics', eventAction: 'RunClicked' }}
                    />
                </div>
            </div>
            <div className={styles.analytics_content}>
                <AnalyticsReport report={getDemeterAnalyticsReportResponse?.report} isLoading={isLoading} />
            </div>
        </div>
    );
};

export default AnalyticsPage;
